/**
 * Admin → Economy Transfers ledger.
 *
 * Read-only audit view of the `economy_transfers` table. Two typeahead
 * filters (`From` + `To`) let an admin scope investigations to a single
 * user or a specific pair in 2 keystrokes.
 *
 * Match mode:
 *   • `or`  (default) — pull every row touching either user (the natural
 *     "show me everything involving this account" flow).
 *   • `and`           — both sides must match (use to verify a specific
 *     suspicious pair-trade).
 */
import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { Field } from "@/components/Atoms";
import { UserSearchInput } from "@/components/admin/UserSearchInput";
import { ScrollText, RefreshCw, X } from "lucide-react";
import { ChilletCoinIcon } from "@/components/ChilletCoinIcon";

export function EconomyTransfers() {
  const toast = useToast();
  const [fromId, setFromId] = useState("");
  const [toId, setToId] = useState("");
  const [match, setMatch] = useState("or");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: "200", match });
      if (fromId) params.set("from_id", fromId);
      if (toId) params.set("to_id", toId);
      const { data } = await api.get(`/admin/economy/transfers?${params.toString()}`);
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error(apiError(e, "Couldn't load transfers ledger"));
    } finally {
      setLoading(false);
    }
  };

  // Auto-refetch whenever either side changes (typeahead onPick fires
  // `onChange` with the canonical user_id, so this naturally re-queries
  // as the admin narrows the search).
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [fromId, toId, match]);

  const clearAll = () => { setFromId(""); setToId(""); };

  return (
    <div className="space-y-4 fade-up" data-testid="economy-transfers-tab">
      <div className="glass-card p-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <ScrollText size={16} className="text-[var(--teal)]" />
            <h3 className="text-lg" style={{ fontFamily: "'Cinzel Decorative', serif" }}>
              Transfers Ledger
            </h3>
          </div>
          <div className="text-xs text-[var(--muted)]">
            {loading ? "Loading…" : `${items.length} shown · ${total.toLocaleString()} total`}
          </div>
        </div>
        <p className="text-xs text-[var(--muted)] mt-1">
          Every <code>/economy/transfer</code> writes a row here. Use the typeaheads to scope by sender, receiver, or both.
        </p>
      </div>

      <div className="grid md:grid-cols-[1fr_1fr_auto_auto] gap-3 items-end">
        <Field label="From (sender)">
          <UserSearchInput
            value={fromId}
            onChange={setFromId}
            onPick={(row) => setFromId(row.discord_id || "")}
            scope="discord_id"
            placeholder="Search Discord/in-game name…"
            dataTestid="transfers-from-search"
          />
        </Field>
        <Field label="To (recipient)">
          <UserSearchInput
            value={toId}
            onChange={setToId}
            onPick={(row) => setToId(row.discord_id || "")}
            scope="discord_id"
            placeholder="Search Discord/in-game name…"
            dataTestid="transfers-to-search"
          />
        </Field>
        <Field label="Match">
          <div className="flex gap-1">
            <button
              type="button"
              onClick={() => setMatch("or")}
              className={`btn btn-sm ${match === "or" ? "" : "btn-secondary"}`}
              data-testid="transfers-match-or"
              title="Match either side — show everything involving either user."
            >
              OR
            </button>
            <button
              type="button"
              onClick={() => setMatch("and")}
              className={`btn btn-sm ${match === "and" ? "" : "btn-secondary"}`}
              data-testid="transfers-match-and"
              title="Both sides must match (specific pair)."
            >
              AND
            </button>
          </div>
        </Field>
        <button
          onClick={clearAll}
          className="btn btn-sm btn-secondary"
          disabled={!fromId && !toId}
          data-testid="transfers-clear"
          title="Clear both filters"
        >
          <X size={12} /> Clear
        </button>
      </div>

      <div className="glass-card p-0 overflow-hidden">
        <div className="flex items-center justify-between px-4 py-2 border-b border-white/5">
          <span className="label !mb-0">Recent</span>
          <button
            onClick={load}
            className="btn btn-sm btn-secondary"
            disabled={loading}
            data-testid="transfers-refresh"
          >
            <RefreshCw size={12} /> Refresh
          </button>
        </div>
        <div className="overflow-x-auto max-h-[60dvh]">
          <table className="w-full min-w-[680px] text-sm" data-testid="transfers-table">
            <thead className="sticky top-0 bg-[#0c0510]/95 backdrop-blur-sm">
              <tr className="text-left text-[10px] uppercase tracking-wider text-[var(--muted)]">
                <th className="px-4 py-2 w-44">When</th>
                <th className="px-4 py-2">From</th>
                <th className="px-4 py-2">To</th>
                <th className="px-4 py-2 text-right">Amount</th>
                <th className="px-4 py-2 text-right">Tax</th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => (
                <tr key={r.id} className="border-t border-white/5 hover:bg-white/5" data-testid={`transfer-row-${r.id}`}>
                  <td className="px-4 py-2 text-xs text-[var(--muted)] font-mono whitespace-nowrap">
                    {fmtTime(r.created_at)}
                  </td>
                  <td className="px-4 py-2">
                    <button
                      onClick={() => setFromId(r.from_id)}
                      className="text-left hover:text-[var(--teal)] transition-colors"
                      title="Filter to this sender"
                    >
                      <div className="truncate max-w-[200px]">{r.from_name || "—"}</div>
                      <div className="text-[10px] font-mono text-[var(--muted)] truncate max-w-[200px]">{r.from_id}</div>
                    </button>
                  </td>
                  <td className="px-4 py-2">
                    <button
                      onClick={() => setToId(r.to_id)}
                      className="text-left hover:text-[var(--teal)] transition-colors"
                      title="Filter to this recipient"
                    >
                      <div className="truncate max-w-[200px]">{r.to_name || "—"}</div>
                      <div className="text-[10px] font-mono text-[var(--muted)] truncate max-w-[200px]">{r.to_id}</div>
                    </button>
                  </td>
                  <td className="px-4 py-2 text-right glow-teal whitespace-nowrap">
                    <span className="inline-flex items-center gap-1">{r.amount.toLocaleString()} <ChilletCoinIcon size={12}/></span>
                  </td>
                  <td className="px-4 py-2 text-right text-[var(--muted)] text-xs whitespace-nowrap">
                    {r.tax.toLocaleString()}
                  </td>
                </tr>
              ))}
              {!loading && items.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-10 text-center text-[var(--muted)] text-sm">
                    {(fromId || toId)
                      ? "No transfers match the current filters."
                      : "No transfers recorded yet — the ledger fills in as players use /economy/transfer."}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function fmtTime(iso) {
  if (!iso) return "—";
  // Accept SQLite "YYYY-MM-DD HH:MM:SS" or ISO 8601; both Date-parse cleanly enough.
  const d = new Date(/Z|[+-]\d{2}:?\d{2}$/.test(iso) ? iso : `${iso.replace(" ", "T")}Z`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    year: "2-digit", month: "short", day: "2-digit",
    hour: "2-digit", minute: "2-digit",
  });
}
